import { useState } from "react";

const TimeEntryFormDialog = ({
  editingEntry = {},
  setEditingEntry,
  loading = {},
  users = [],
  projects = [],
  scaffoldIds = [],
  workTypes = [],
  compensationTypes = [],
  formErrors = {},
  selectedUsers = [],
  handleUserChange,
  handleProjectChange,
  saveEditedEntry,
  createMultipleEntries,
}) => {
  const [dropdownStates, setDropdownStates] = useState({
    user: false,
    project: false,
    scaffold: false,
    workType: false,
    compensation: false,
    status: false,
  });

  const toggleDropdown = (dropdown) => {
    setDropdownStates({
      ...dropdownStates,
      [dropdown]: !dropdownStates[dropdown],
    });
  };

  const closeAllDropdowns = () => {
    setDropdownStates({
      user: false,
      project: false,
      scaffold: false,
      workType: false,
      compensation: false,
      status: false,
    });
  };

  const filteredScaffoldIds =
    scaffoldIds?.filter(
      (scaffold) =>
        !editingEntry?.projectId ||
        scaffold.projectId === editingEntry.projectId
    ) || [];

  const selectedUserName = () => {
    if (editingEntry?.id?.startsWith("new")) {
      return selectedUsers.length === 1
        ? users?.find((u) => u._id === selectedUsers[0])?.name || "Select User"
        : selectedUsers.length > 1
        ? `${selectedUsers.length} users selected`
        : "Select User";
    } else {
      return (
        users?.find((u) => u._id === editingEntry.userId)?.name || "Select User"
      );
    }
  };

  const selectedProjectName = () => {
    return (
      projects.find((p) => p._id === editingEntry.projectId)?.projectName ||
      "Select Project"
    );
  };

  const selectedScaffoldName = () => {
    return (
      filteredScaffoldIds.find((s) => s._id === editingEntry.scaffoldId)
        ?.scaffolderowner || "Select Scaffold ID"
    );
  };

  const selectedWorkTypeName = () => {
    return (
      workTypes.find((w) => w._id === editingEntry.workTypeId)?.name ||
      "Select Work Type"
    );
  };

  const selectedCompensationName = () => {
    if (!editingEntry.compensationTypeId) return "None";
    return (
      compensationTypes.find((c) => c._id === editingEntry.compensationTypeId)
        ?.name || "None"
    );
  };

  function getLastSixDigits(userId) {
    try {  
      return userId.slice(-6); 
    } catch (error) {
      console.error(error.message);
      return null;
    }
  }
  

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center ${
        Boolean(editingEntry) ? "block" : "hidden"
      }`}
    >
      <div
        className="fixed inset-0 bg-black/50 "
        onClick={() => {
          closeAllDropdowns();
          setEditingEntry(null);
        }}
      ></div>

      <div
        className="relative w-full max-w-3xl h-[90vh] overflow-auto  bg-white rounded-lg shadow-lg"
        onClick={closeAllDropdowns}
      >
        <div className="px-6 py-4 border-b">
          <h2 className="text-xl font-semibold">
            {editingEntry?.id?.startsWith("new")
              ? "Add New Time Entry"
              : "Edit Time Entry"}
          </h2>
        </div>

        <div className="px-6 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <div className="space-y-1">
              <label htmlFor="editUser" className="block text-sm font-medium">
                User <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <button
                  type="button"
                  className={`w-full p-2.5 border rounded-md bg-white text-left flex justify-between items-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    formErrors.userId ? "border-red-500" : "border-gray-300"
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleDropdown("user");
                  }}
                  disabled={loading?.users}
                >
                  <span className="truncate">{selectedUserName()}</span>
                  <svg
                    className="w-4 h-4 ml-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>

                {dropdownStates?.user && !loading.users && (
                  <div
                    className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md border border-gray-300 max-h-60 overflow-y-auto"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {editingEntry?.id?.startsWith("new") ? (
                      users?.length > 0 ? (
                        users?.map((user) => (
                          <div
                            key={user._id}
                            className={`p-2 hover:bg-blue-100 cursor-pointer flex items-center ${
                              selectedUsers.includes(user._id)
                                ? "bg-blue-50"
                                : ""
                            }`}
                            onClick={() => {
                              const isSelected = selectedUsers.includes(
                                user._id
                              );
                              const newSelectedUsers = isSelected
                                ? selectedUsers.filter((id) => id !== user._id)
                                : [...selectedUsers, user._id];

                              handleUserChange(newSelectedUsers);

                              if (
                                !editingEntry?.id?.startsWith("new") ||
                                newSelectedUsers.length === 1
                              ) {
                                setDropdownStates({
                                  ...dropdownStates,
                                  user: false,
                                });
                              }
                            }}
                          >
                            {editingEntry?.id?.startsWith("new") && (
                              <input
                                type="checkbox"
                                className="mr-2"
                                checked={selectedUsers.includes(user._id)}
                                onChange={() => {}}
                              />
                            )}
                            {user.name} { user._id && `(${getLastSixDigits(user._id)})`}
                          </div>
                        ))
                      ) : (
                        <div className="p-2 text-gray-500">No users found</div>
                      )
                    ) : users?.length > 0 ? (
                      users?.map((user) => (
                        <div
                          key={user._id}
                          className={`p-2 hover:bg-blue-100 cursor-pointer ${
                            editingEntry.userId === user._id ? "bg-blue-50" : ""
                          }`}
                          onClick={() => {
                            handleUserChange(user._id, user.name);
                            setDropdownStates({
                              ...dropdownStates,
                              user: false,
                            });
                          }}
                        >
                          {user.name}
                        </div>
                      ))
                    ) : (
                      <div className="p-2 text-gray-500">No users found</div>
                    )}
                  </div>
                )}

                {loading?.users && (
                  <div className="absolute right-2 top-1/2 -translate-y-1/2">
                    <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
              </div>
              {formErrors.userId && (
                <p className="text-sm text-red-500">{formErrors.userId}</p>
              )}
              {editingEntry?.id?.startsWith("new") && (
                <p className="text-xs text-gray-500">
                  You can select multiple users to create entries for all of
                  them at once
                </p>
              )}
              {editingEntry?.id?.startsWith("new") &&
                selectedUsers?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {selectedUsers?.map((value) => {
                      const user = users?.find((u) => u._id === value);
                      return (
                        <span
                          key={value}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded"
                        >
                          {user?.name || "Unknown"}{" "}
                          {user?._id && `(${getLastSixDigits(user._id)})`}
                        </span>
                      );
                    })}
                  </div>
                )}
            </div>

            <div className="space-y-1">
              <label
                htmlFor="editProject"
                className="block text-sm font-medium"
              >
                Project <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <button
                  type="button"
                  className={`w-full p-2.5 border rounded-md bg-white text-left flex justify-between items-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    formErrors.projectId ? "border-red-500" : "border-gray-300"
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleDropdown("project");
                  }}
                  disabled={!editingEntry.userId || loading.projects}
                >
                  <span className="truncate">{selectedProjectName()}</span>
                  <svg
                    className="w-4 h-4 ml-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>

                {dropdownStates.project && !loading.projects && (
                  <div
                    className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md border border-gray-300 max-h-60 overflow-y-auto"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {projects?.length > 0 ? (
                      projects.map((project) => (
                        <div
                          key={project._id}
                          className={`p-2 hover:bg-blue-100 cursor-pointer ${
                            editingEntry.projectId === project._id
                              ? "bg-blue-50"
                              : ""
                          }`}
                          onClick={() => {
                            handleProjectChange(
                              project._id,
                              project.projectName
                            );
                            setDropdownStates({
                              ...dropdownStates,
                              project: false,
                            });
                          }}
                        >
                          {project.projectName}
                        </div>
                      ))
                    ) : (
                      <div className="p-2 text-gray-500">
                        {editingEntry.userId
                          ? "No projects found"
                          : "Select a user first"}
                      </div>
                    )}
                  </div>
                )}

                {loading.projects && (
                  <div className="absolute right-2 top-1/2 -translate-y-1/2">
                    <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
              </div>
              {formErrors.projectId && (
                <p className="text-sm text-red-500">{formErrors.projectId}</p>
              )}
            </div>

            <div className="space-y-1">
              <label
                htmlFor="editScaffold"
                className="block text-sm font-medium"
              >
                Scaffold ID <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <button
                  type="button"
                  className={`w-full p-2.5 border rounded-md bg-white text-left flex justify-between items-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    formErrors.scaffoldId ? "border-red-500" : "border-gray-300"
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleDropdown("scaffold");
                  }}
                  disabled={!editingEntry.projectId || loading.scaffolds}
                >
                  <span className="truncate">{selectedScaffoldName()}</span>
                  <svg
                    className="w-4 h-4 ml-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>

                {dropdownStates.scaffold && !loading.scaffolds && (
                  <div
                    className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md border border-gray-300 max-h-60 overflow-y-auto"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {filteredScaffoldIds?.length > 0 ? (
                      filteredScaffoldIds.map((scaffold) => (
                        <div
                          key={scaffold._id}
                          className={`p-2 hover:bg-blue-100 cursor-pointer ${
                            editingEntry.scaffoldId === scaffold._id
                              ? "bg-blue-50"
                              : ""
                          }`}
                          onClick={() => {
                            setEditingEntry({
                              ...editingEntry,
                              scaffoldId: scaffold._id,
                              scaffoldName: scaffold.scaffolderowner,
                            });
                            setDropdownStates({
                              ...dropdownStates,
                              scaffold: false,
                            });
                          }}
                        >
                          {scaffold.scaffolderowner}
                        </div>
                      ))
                    ) : (
                      <div className="p-2 text-gray-500">
                        {editingEntry.projectId
                          ? "No scaffold IDs found"
                          : "Select a project first"}
                      </div>
                    )}
                  </div>
                )}

                {loading.scaffolds && (
                  <div className="absolute right-2 top-1/2 -translate-y-1/2">
                    <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
              </div>
              {formErrors.scaffoldId && (
                <p className="text-sm text-red-500">{formErrors.scaffoldId}</p>
              )}
            </div>

            <div className="space-y-1">
              <label htmlFor="editDate" className="block text-sm font-medium">
                Date <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  id="editDate"
                  type="date"
                  className={`w-full p-2.5 border rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    formErrors.date ? "border-red-500" : "border-gray-300"
                  }`}
                  value={editingEntry.date || ""}
                  onChange={(e) => {
                    setEditingEntry({ ...editingEntry, date: e.target.value });
                  }}
                />
              </div>
              {formErrors.date && (
                <p className="text-sm text-red-500">{formErrors.date}</p>
              )}
            </div>

            <div className="space-y-1">
              <label htmlFor="editHours" className="block text-sm font-medium">
                Hours <span className="text-red-500">*</span>
              </label>
              <input
                id="editHours"
                type="number"
                className={`w-full p-2.5 border rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  formErrors.totalHours ? "border-red-500" : "border-gray-300"
                }`}
                value={editingEntry.totalHours || ""}
                onChange={(e) => {
                  setEditingEntry({
                    ...editingEntry,
                    totalHours: Number(e.target.value),
                  });
                }}
                min={0}
                step={0.5}
              />
              {formErrors.totalHours && (
                <p className="text-sm text-red-500">{formErrors.totalHours}</p>
              )}
            </div>

            <div className="space-y-1">
              <label
                htmlFor="editWorkType"
                className="block text-sm font-medium"
              >
                Work Type <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <button
                  type="button"
                  className={`w-full p-2.5 border rounded-md bg-white text-left flex justify-between items-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    formErrors.workTypeId ? "border-red-500" : "border-gray-300"
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleDropdown("workType");
                  }}
                  disabled={loading.workTypes}
                >
                  <span className="truncate">{selectedWorkTypeName()}</span>
                  <svg
                    className="w-4 h-4 ml-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>

                {dropdownStates.workType && !loading.workTypes && (
                  <div
                    className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md border border-gray-300 max-h-60 overflow-y-auto"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {workTypes?.length > 0 ? (
                      workTypes.map((type) => (
                        <div
                          key={type._id}
                          className={`p-2 hover:bg-blue-100 cursor-pointer ${
                            editingEntry.workTypeId === type._id
                              ? "bg-blue-50"
                              : ""
                          }`}
                          onClick={() => {
                            setEditingEntry({
                              ...editingEntry,
                              workTypeId: type._id,
                              workTypeName: type.name,
                            });
                            setDropdownStates({
                              ...dropdownStates,
                              workType: false,
                            });
                          }}
                        >
                          {type.name}
                        </div>
                      ))
                    ) : (
                      <div className="p-2 text-gray-500">
                        No work types found
                      </div>
                    )}
                  </div>
                )}

                {loading.workTypes && (
                  <div className="absolute right-2 top-1/2 -translate-y-1/2">
                    <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
              </div>
              {formErrors.workTypeId && (
                <p className="text-sm text-red-500">{formErrors.workTypeId}</p>
              )}
            </div>

            <div className="space-y-1">
              <label
                htmlFor="editCompensation"
                className="block text-sm font-medium"
              >
                Additional Compensation
              </label>
              <div className="relative">
                <button
                  type="button"
                  className="w-full p-2.5 border border-gray-300 rounded-md bg-white text-left flex justify-between items-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleDropdown("compensation");
                  }}
                  disabled={loading.compensationTypes}
                >
                  <span className="truncate">{selectedCompensationName()}</span>
                  <svg
                    className="w-4 h-4 ml-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>

                {dropdownStates.compensation && !loading.compensationTypes && (
                  <div
                    className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md border border-gray-300 max-h-60 overflow-y-auto"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div
                      className={`p-2 hover:bg-blue-100 cursor-pointer ${
                        !editingEntry.compensationTypeId ? "bg-blue-50" : ""
                      }`}
                      onClick={() => {
                        setEditingEntry({
                          ...editingEntry,
                          compensationTypeId: null,
                          compensationTypeName: null,
                        });
                        setDropdownStates({
                          ...dropdownStates,
                          compensation: false,
                        });
                      }}
                    >
                      None
                    </div>
                    {compensationTypes?.length > 0 ? (
                      compensationTypes.map((type) => (
                        <div
                          key={type._id}
                          className={`p-2 hover:bg-blue-100 cursor-pointer ${
                            editingEntry.compensationTypeId === type._id
                              ? "bg-blue-50"
                              : ""
                          }`}
                          onClick={() => {
                            setEditingEntry({
                              ...editingEntry,
                              compensationTypeId: type._id,
                              compensationTypeName: type.name,
                            });
                            setDropdownStates({
                              ...dropdownStates,
                              compensation: false,
                            });
                          }}
                        >
                          {type.name}
                        </div>
                      ))
                    ) : (
                      <div className="p-2 text-gray-500">
                        No compensation types found
                      </div>
                    )}
                  </div>
                )}

                {loading.compensationTypes && (
                  <div className="absolute right-2 top-1/2 -translate-y-1/2">
                    <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-1 col-span-1 md:col-span-2">
              <label
                htmlFor="editComments"
                className="block text-sm font-medium"
              >
                Comments
              </label>
              <textarea
                id="editComments"
                className="w-full p-2.5 border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows={4}
                value={editingEntry.comments || ""}
                onChange={(e) =>
                  setEditingEntry({ ...editingEntry, comments: e.target.value })
                }
              ></textarea>
            </div>

            <div className="space-y-1 col-span-1 md:col-span-2">
              <label htmlFor="editStatus" className="block text-sm font-medium">
                Approval Status
              </label>
              <div className="relative">
                <button
                  type="button"
                  className="w-full p-2.5 border border-gray-300 rounded-md bg-white text-left flex justify-between items-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleDropdown("status");
                  }}
                >
                  <span className="truncate">
                    {editingEntry.approvalStatus === "approved"
                      ? "Approved"
                      : "Pending"}
                  </span>
                  <svg
                    className="w-4 h-4 ml-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>

                {dropdownStates.status && (
                  <div
                    className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md border border-gray-300"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div
                      className={`p-2 hover:bg-blue-100 cursor-pointer ${
                        editingEntry.approvalStatus === "pending"
                          ? "bg-blue-50"
                          : ""
                      }`}
                      onClick={() => {
                        setEditingEntry({
                          ...editingEntry,
                          approvalStatus: "pending",
                        });
                        setDropdownStates({ ...dropdownStates, status: false });
                      }}
                    >
                      Pending
                    </div>
                    <div
                      className={`p-2 hover:bg-blue-100 cursor-pointer ${
                        editingEntry.approvalStatus === "approved"
                          ? "bg-blue-50"
                          : ""
                      }`}
                      onClick={() => {
                        setEditingEntry({
                          ...editingEntry,
                          approvalStatus: "approved",
                        });
                        setDropdownStates({ ...dropdownStates, status: false });
                      }}
                    >
                      Approved
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t flex justify-end space-x-2">
          <button
            onClick={() => setEditingEntry(null)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Cancel
          </button>
          <button
            onClick={
              editingEntry?.id?.startsWith("new") && selectedUsers?.length > 1
                ? createMultipleEntries
                : saveEditedEntry
            }
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading.saveEntry}
          >
            {loading.saveEntry ? (
              <span className="flex items-center">
                <span className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                Saving...
              </span>
            ) : editingEntry?.id?.startsWith("new") &&
              selectedUsers?.length > 1 ? (
              `Create ${selectedUsers.length} Entries`
            ) : (
              "Save"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TimeEntryFormDialog;
